import React, { useEffect, useMemo, useState } from 'react'

import api from '../../../common/api'
import BigBreadcrumbs from '../../../components/layout/navigation/components/BigBreadcrumbs'
import WidgetGrid from '../../../components/layout/widgets/WidgetGrid'
import JarvisWidget from '../../../components/layout/widgets/JarvisWidget'
import DismissibleAlert from '../../../common/components/common/DismissibleAlert'

import styles from './List.module.css'

function PromotionSettingsList() {
  const [items, setItems] = useState([])
  const [draftPrices, setDraftPrices] = useState({})
  const [rowErrors, setRowErrors] = useState({})
  const [loading, setLoading] = useState(true)
  const [savingById, setSavingById] = useState({})
  const [savingStatusById, setSavingStatusById] = useState({})
  const [globalError, setGlobalError] = useState('')
  const [successMessage, setSuccessMessage] = useState('')

  const normalizedItems = useMemo(
    () => (Array.isArray(items) ? items : []),
    [items]
  )

  const loadItems = () => {
    setLoading(true)
    setGlobalError('')
    return api
      .getPromotionSettings()
      .then(res => {
        const rows = Array.isArray(res?.data) ? res.data : []
        setItems(rows)
        setDraftPrices(
          rows.reduce((acc, row) => {
            acc[row.id] = row.price
            return acc
          }, {})
        )
      })
      .catch(err => {
        setGlobalError(
          err?.response?.data?.message ||
            'Failed to load promotion settings. Please try again.'
        )
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadItems()
  }, [])

  const handlePriceChange = (id, value) => {
    setSuccessMessage('')
    setDraftPrices(prev => ({ ...prev, [id]: value }))
    if (rowErrors[id]) {
      setRowErrors(prev => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    }
  }

  const saveRow = id => {
    const rawValue = draftPrices[id]
    const parsedPrice = Number(rawValue)

    if (rawValue === '' || Number.isNaN(parsedPrice) || parsedPrice < 0) {
      setRowErrors(prev => ({
        ...prev,
        [id]: 'Price must be a valid number greater than or equal to 0.',
      }))
      return
    }

    setSavingById(prev => ({ ...prev, [id]: true }))
    setGlobalError('')

    api
      .updatePromotionSetting(id, { price: parsedPrice })
      .then(res => {
        const updated = res?.data
        setItems(prev =>
          prev.map(item => (item.id === id ? { ...item, ...updated } : item))
        )
        setSuccessMessage('Promotion setting updated successfully.')
      })
      .catch(err => {
        setGlobalError(
          err?.response?.data?.message ||
            'Failed to update promotion setting. Please try again.'
        )
      })
      .finally(() => {
        setSavingById(prev => ({ ...prev, [id]: false }))
      })
  }

  const toggleStatus = (id, currentStatus) => {
    const nextStatus = currentStatus === 1 ? 0 : 1

    setSavingStatusById(prev => ({ ...prev, [id]: true }))
    setGlobalError('')
    setSuccessMessage('')

    api
      .updatePromotionSettingStatus(id, { status: nextStatus })
      .then(res => {
        const updated = res?.data
        setItems(prev =>
          prev.map(item => (item.id === id ? { ...item, ...updated } : item))
        )
        setSuccessMessage('Promotion setting status updated successfully.')
      })
      .catch(err => {
        setGlobalError(
          err?.response?.data?.message ||
            'Failed to update promotion setting status. Please try again.'
        )
      })
      .finally(() => {
        setSavingStatusById(prev => ({ ...prev, [id]: false }))
      })
  }

  return (
    <div id="content" className={styles.wrapper}>
      <div className="row">
        <BigBreadcrumbs
          items={['Promotion Settings']}
          icon="cog"
          className="col-xs-12 col-sm-7 col-md-7 col-lg-4"
        />
      </div>

      {globalError && (
        <DismissibleAlert
          type="danger"
          className={styles.globalError}
          resetKey={globalError}
          onDismiss={() => setGlobalError('')}
        >
          {globalError}
        </DismissibleAlert>
      )}
      {successMessage && (
        <DismissibleAlert
          type="success"
          className={styles.successBox}
          resetKey={successMessage}
          onDismiss={() => setSuccessMessage('')}
        >
          {successMessage}
        </DismissibleAlert>
      )}

      <WidgetGrid>
        <div className="row">
          <article className="col-sm-12">
            <JarvisWidget
              editbutton={false}
              togglebutton={false}
              fullscreenbutton={false}
              deletebutton={false}
              color="darken"
            >
              <header>
                <span className="widget-icon">
                  <i className="fa fa-money" />
                </span>
                <h2>Promotion Settings</h2>
              </header>
              <div>
                <div className="widget-body no-padding">
                  {loading ? (
                    <div style={{ padding: 20 }}>Loading promotion settings...</div>
                  ) : (
                    <table className={`table table-striped table-bordered ${styles.table}`}>
                      <thead>
                        <tr>
                          <th style={{ width: '80px' }}>ID</th>
                          <th>Tier</th>
                          <th>Placement</th>
                          <th style={{ width: '220px' }}>Price (USD / week)</th>
                          <th style={{ width: '120px' }}>Status</th>
                          <th style={{ width: '140px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {normalizedItems.map(item => (
                          <tr key={item.id}>
                            <td>{item.id}</td>
                            <td className={styles.tierCell}>{item.label}</td>
                            <td className={styles.placementCell}>{item.placement}</td>
                            <td>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className={`form-control ${styles.priceInput}`}
                                value={draftPrices[item.id] ?? ''}
                                onChange={e =>
                                  handlePriceChange(item.id, e.target.value)
                                }
                              />
                              {rowErrors[item.id] && (
                                <div className={styles.errorText}>{rowErrors[item.id]}</div>
                              )}
                            </td>
                            <td>
                              <button
                                className={`btn ${item.status === 1 ? 'btn-success' : 'btn-danger'}`}
                                onClick={() => toggleStatus(item.id, item.status)}
                                disabled={!!savingStatusById[item.id]}
                              >
                                {savingStatusById[item.id]
                                  ? 'Updating...'
                                  : item.status === 1
                                    ? 'Active'
                                    : 'Inactive'}
                              </button>
                            </td>
                            <td>
                              <button
                                className="btn btn-primary"
                                onClick={() => saveRow(item.id)}
                                disabled={!!savingById[item.id]}
                              >
                                {savingById[item.id] ? 'Saving...' : 'Save'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </JarvisWidget>
          </article>
        </div>
      </WidgetGrid>
    </div>
  )
}

export default PromotionSettingsList
